import { useState, useCallback, useEffect } from 'react';

/**
 * Get API URL - always use same origin for cross-environment compatibility
 */
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

/**
 * Custom hook for managing editable category tabs
 * Handles adding, removing, renaming, and reordering categories
 * Persists state to MongoDB database (with localStorage fallback for offline)
 */
const useCategoryTabs = (storageKey, defaultCategories = [], options = {}) => {
  const { userId = 'default_user', syncToDatabase = true } = options;
  
  const [categories, setCategories] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : defaultCategories;
    } catch {
      return defaultCategories;
    }
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  const [editingCategory, setEditingCategory] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Load categories from database on mount
  useEffect(() => {
    if (!syncToDatabase) {
      setIsLoading(false);
      return;
    }
    
    let isCancelled = false;
    const abortController = new AbortController();
    const apiUrl = getApiUrl();
    
    const loadFromDatabase = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/api/user-profile/settings/${storageKey}?user_id=${userId}`,
          { signal: abortController.signal }
        );
        
        if (isCancelled || !response.ok) {
          setIsLoading(false);
          return;
        }
        
        const data = await response.json();
        
        if (isCancelled) return;
        
        if (data.exists && data.value) {
          setCategories(data.value);
          // Also update localStorage as cache
          localStorage.setItem(storageKey, JSON.stringify(data.value));
          console.log(`✅ Loaded ${storageKey} from database:`, data.value.length, 'categories');
        } else {
          // No database entry, use localStorage or defaults
          console.log(`📦 No database entry for ${storageKey}, using localStorage/defaults`);
        }
      } catch (error) {
        if (!isCancelled && error.name !== 'AbortError') {
          console.warn(`⚠️ Failed to load ${storageKey} from database, using localStorage:`, error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    
    loadFromDatabase();
    
    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [storageKey, userId, syncToDatabase]);

  // Save to both localStorage and database
  const saveCategories = useCallback(async (newCategories) => {
    setCategories(newCategories);
    localStorage.setItem(storageKey, JSON.stringify(newCategories));
    
    if (syncToDatabase) {
      setIsSyncing(true);
      const apiUrl = getApiUrl();
      try {
        const response = await fetch(`${apiUrl}/api/user-profile/settings/${storageKey}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            key: storageKey,
            value: newCategories,
            user_id: userId
          })
        });
        
        if (response.ok) {
          console.log(`✅ Saved ${storageKey} to database`);
        } else {
          console.warn(`⚠️ Failed to save ${storageKey} to database`);
        }
      } catch (error) {
        console.warn(`⚠️ Error saving ${storageKey} to database:`, error);
      } finally {
        setIsSyncing(false);
      }
    }
  }, [storageKey, userId, syncToDatabase]);

  // Add a new category
  const addCategory = useCallback((category) => {
    if (!category || categories.some(c => 
      (typeof c === 'string' ? c : c.id) === (typeof category === 'string' ? category : category.id)
    )) {
      return false;
    }
    saveCategories([...categories, category]);
    return true;
  }, [categories, saveCategories]);

  // Remove a category
  const removeCategory = useCallback((categoryId) => {
    const newCategories = categories.filter(c => 
      (typeof c === 'string' ? c : c.id) !== categoryId
    );
    saveCategories(newCategories);
  }, [categories, saveCategories]);

  // Start editing a category
  const startEditing = useCallback((categoryId) => {
    const category = categories.find(c => 
      (typeof c === 'string' ? c : c.id) === categoryId
    );
    if (category) {
      setEditingCategory(categoryId);
      setEditValue(typeof category === 'string' ? category : category.label || category.name);
    }
  }, [categories]);

  // Save edited category
  const saveEditing = useCallback(() => {
    if (!editingCategory || !editValue.trim()) {
      setEditingCategory(null);
      setEditValue('');
      return;
    }

    const newCategories = categories.map(c => {
      const id = typeof c === 'string' ? c : c.id;
      if (id === editingCategory) {
        if (typeof c === 'string') {
          return editValue.trim();
        } else {
          return { ...c, label: editValue.trim(), name: editValue.trim() };
        }
      }
      return c;
    });

    saveCategories(newCategories);
    setEditingCategory(null);
    setEditValue('');
  }, [editingCategory, editValue, categories, saveCategories]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditingCategory(null);
    setEditValue('');
  }, []);

  // Reorder categories
  const reorderCategories = useCallback((fromIndex, toIndex) => {
    const newCategories = [...categories];
    const [removed] = newCategories.splice(fromIndex, 1);
    newCategories.splice(toIndex, 0, removed);
    saveCategories(newCategories);
  }, [categories, saveCategories]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    saveCategories(defaultCategories);
  }, [defaultCategories, saveCategories]);

  return {
    categories,
    setCategories: saveCategories,
    addCategory,
    removeCategory,
    editingCategory,
    editValue,
    setEditValue,
    startEditing,
    saveEditing,
    cancelEditing,
    reorderCategories,
    resetToDefaults,
    isLoading,
    isSyncing
  };
};

export default useCategoryTabs;
